import { Component, inject, Signal, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatError, MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatLabel } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { APPLICATION_TYPE } from '@core';
import { AuthorizedUser } from '@models';
import { Store } from '@ngrx/store';
import { AuthActions, AuthSelectors } from '@ngrx-store';
import { AuthService } from '@services';
import { MainLogoComponent, NotificationsService } from '@ui';
import { tap } from 'rxjs';

import { emailValidationPattern } from '../../constants';
import { TotpCodeComponent } from '../../containers';

type SignInStep = 'credentials' | 'totp';

const RECOVERY_CODE_PATTERN = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/;
const TOTP_CODE_LENGTH = 6;

@Component({
  imports: [
    MainLogoComponent,
    MatCard,
    MatCardContent,
    ReactiveFormsModule,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    MatError,
    RouterLink,
    MatProgressSpinner,
    MatPrefix,
    MatSuffix,
    TotpCodeComponent,
  ],
  selector: 'avs-li-sign-in',
  standalone: true,
  templateUrl: './sign-in.component.html',
})
export class SignInComponent {
  protected form: FormGroup;
  protected hide = signal(true);
  protected isBlocked = signal(false);
  protected isLoading = signal(false);
  protected isLoggedIn: Signal<boolean | undefined>;
  protected isMobile = inject(APPLICATION_TYPE) === 'mobile';
  protected isVerifyDisabled = signal(true);
  protected isVerifying = signal(false);
  protected step = signal('credentials' as SignInStep);
  protected totpHasError = signal(false);
  protected totpToken = signal('');
  protected useRecoveryCode = signal(false);

  private authService = inject(AuthService);
  private digitsInput = viewChild(TotpCodeComponent);
  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store);

  constructor() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(emailValidationPattern)]],
      password: ['', [Validators.required]],
    });
    this.isLoggedIn = toSignal(this.store.select(AuthSelectors.isLoggedIn));
  }

  protected login() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();

      return;
    }
    this.isLoading.set(true);
    const val = this.form.value;

    this.authService.login(val.email, val.password).subscribe({
      error: (error: { message: string; name: string; status: number; }) => {
        this.isLoading.set(false);
        this.notificationService.showErrorMessage(
          error.message?.toLowerCase() === 'unauthorized'
            ? 'Entered data is not correct'
            : error.message,
        );
        if (error.status === 403) {
          this.isBlocked.set(true);
        }
      },
      next: async response => {
        this.isLoading.set(false);

        if ('pendingTotp' in response && response.pendingTotp) {
          this.step.set('totp');

          return;
        }

        if ('totpEnrollmentRequired' in response && response.totpEnrollmentRequired) {
          this.notificationService.showErrorMessage(
            'Two-factor authentication setup is required. Please contact your administrator.',
          );

          return;
        }

        this.store.dispatch(AuthActions.login({ user: response as AuthorizedUser }));
        await this.router.navigateByUrl(this.route.snapshot.queryParams['returnUrl'] || 'dashboard');
      },
    });
  }

  protected onTotpTokenChange(value: string) {
    this.totpToken.set(value);
    this.totpHasError.set(false);
    this.updateVerifyDisabled();
  }

  protected toggleRecoveryCode() {
    this.useRecoveryCode.set(!this.useRecoveryCode());
    this.totpToken.set('');
    this.totpHasError.set(false);
    this.updateVerifyDisabled();
    this.digitsInput()?.reset();
  }

  protected verifyTotp() {
    if (this.isVerifyDisabled()) {
      return;
    }

    this.isVerifying.set(true);
    this.updateVerifyDisabled();
    const val = this.form.value;

    this.authService
      .login(val.email, val.password, this.totpToken())
      .pipe(
        tap(user => {
          this.store.dispatch(AuthActions.login({ user: user as AuthorizedUser }));
        }),
      )
      .subscribe({
        error: () => {
          this.isVerifying.set(false);
          this.totpHasError.set(true);
          this.updateVerifyDisabled();
        },
        next: async () => {
          this.isVerifying.set(false);
          this.updateVerifyDisabled();

          await this.router.navigateByUrl(this.route.snapshot.queryParams['returnUrl'] || 'dashboard');
        },
      });
  }

  private updateVerifyDisabled() {
    const isValid = this.useRecoveryCode()
      ? RECOVERY_CODE_PATTERN.test(this.totpToken())
      : this.totpToken().length === TOTP_CODE_LENGTH;

    this.isVerifyDisabled.set(!isValid || this.isVerifying() || this.totpHasError());
  }
}
